import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import OwnerModal from '../components/owners/OwnerModal';
import DeleteConfirmModal from '../components/owners/DeleteConfirmModal';

const Owners = () => {
    const { isDark } = useTheme();
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingOwner, setEditingOwner] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Mobile check
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchOwners = useCallback(async () => {
        try {
            const response = await fetch('/api/v1/owners', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setOwners(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching owners:', error);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOwners();
    }, [fetchOwners]);

    const handleDelete = async (id) => {
        try {
            await fetch(`/api/v1/owners/${id}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            setOwners(owners.filter(owner => owner.id !== id));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting owner:', error);
        }
    };

    const handleSave = (savedOwner) => {
        if (editingOwner) {
            // Update existing owner
            setOwners(owners.map(owner =>
                owner.id === savedOwner.id ? { ...savedOwner, full_name: `${savedOwner.first_name} ${savedOwner.last_name}` } : owner
            ));
        } else {
            // Add new owner
            setOwners([...owners, { ...savedOwner, full_name: `${savedOwner.first_name} ${savedOwner.last_name}` }]);
        }
        setShowModal(false);
        setEditingOwner(null);
    };

    const filteredOwners = owners.filter(owner =>
        owner.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        owner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        owner.phone_number.includes(searchTerm)
    );

    const styles = {
        container: {
            padding: '1rem 0',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            marginBottom: '2rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '1rem' : '0',
        },
        title: {
            fontSize: '2rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: isMobile ? '0.5rem' : '0',
        },
        searchContainer: {
            flex: 1,
            maxWidth: isMobile ? '100%' : '400px',
            marginRight: isMobile ? '0' : '1rem',
            marginBottom: isMobile ? '0.5rem' : '0',
        },
        searchInput: {
            width: '100%',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#1f2937' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        addButton: {
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
            transition: '0.2s',
            width: isMobile ? '100%' : 'auto',
        },
        tableContainer: {
            background: isMobile ? 'transparent' : (isDark ? '#1e1e1e' : '#ffffff'),
            borderRadius: '1rem',
            marginLeft: isMobile ? '0' : '1rem',
            marginRight: isMobile ? '0' : '1rem',
            padding: isMobile ? '0' : '0', // Reset check
            boxShadow: isMobile ? 'none' : (isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'),
            overflowX: 'auto',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            textAlign: 'left',
            padding: '1rem',
            borderBottom: `2px solid ${isDark ? '#333' : '#e5e7eb'}`,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
        td: {
            padding: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#d1d5db' : '#374151',
        },
        actionButton: {
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: 600,
            marginRight: '0.5rem',
            transition: '0.2s',
        },
        editButton: {
            background: isDark ? '#1e40af' : '#3b82f6',
            color: 'white',
        },
        deleteButton: {
            background: isDark ? '#991b1b' : '#ef4444',
            color: 'white',
        },
        emptyState: {
            textAlign: 'center',
            padding: '4rem 2rem',
            color: isDark ? '#9ca3af' : '#6b7280',
        },
        // Mobile Card Styles
        card: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '1.25rem',
            marginBottom: '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            margin: '0 1rem 1rem 1rem',
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            paddingBottom: '0.75rem',
        },
        cardRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
        },
        cardLabel: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontWeight: 500,
        },
        cardValue: {
            color: isDark ? '#d1d5db' : '#374151',
            fontWeight: 600,
            textAlign: 'right',
        },
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={{ ...styles.emptyState, minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div>Loading owners...</div>
                </div>
            </div>
        );
    }

    const renderMobileCard = (owner) => (
        <div key={owner.id} style={styles.card}>
            <div style={styles.cardHeader}>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: isDark ? '#fff' : '#1f2937' }}>
                    {owner.full_name}
                </div>
                <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: isDark ? '#374151' : '#e5e7eb',
                    color: isDark ? '#d1d5db' : '#374151',
                }}>
                    {owner.owner_type}
                </span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Cars Owned</span>
                <span style={{ ...styles.cardValue, color: '#3b82f6', fontWeight: 'bold' }}>
                    {owner.cars_count}
                </span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Phone</span>
                <span style={styles.cardValue}>{owner.phone_number}</span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Email</span>
                <span style={styles.cardValue}>{owner.email}</span>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <button
                    onClick={() => {
                        setEditingOwner(owner);
                        setShowModal(true);
                    }}
                    style={{ ...styles.actionButton, ...styles.editButton, flex: 1, textAlign: 'center', marginRight: 0 }}
                >
                    Edit
                </button>
                <button
                    onClick={() => setDeleteConfirm(owner)}
                    style={{ ...styles.actionButton, ...styles.deleteButton, flex: 1, textAlign: 'center', marginRight: 0 }}
                >
                    Delete
                </button>
            </div>
        </div>
    );

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Owners</h1>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end', width: isMobile ? '100%' : 'auto', flexDirection: isMobile ? 'column' : 'row' }}>
                    <div style={styles.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search owners..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                    <button
                        onClick={() => {
                            setEditingOwner(null);
                            setShowModal(true);
                        }}
                        style={styles.addButton}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                        + Add New Owner
                    </button>
                </div>
            </div>

            <div style={styles.tableContainer}>
                {filteredOwners.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                            {searchTerm ? 'No owners found' : 'No owners yet'}
                        </div>
                        <div>
                            {searchTerm ? 'Try a different search term' : 'Click "Add New Owner" to get started'}
                        </div>
                    </div>
                ) : isMobile ? (
                    <div>
                        {filteredOwners.map(renderMobileCard)}
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Name</th>
                                <th style={styles.th}>Type</th>
                                <th style={styles.th}>Cars</th>
                                <th style={styles.th}>Phone</th>
                                <th style={styles.th}>Email</th>
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOwners.map((owner) => (
                                <tr key={owner.id}>
                                    <td style={styles.td}>{owner.full_name}</td>
                                    <td style={styles.td}>{owner.owner_type}</td>
                                    <td style={styles.td}>{owner.cars_count}</td>
                                    <td style={styles.td}>{owner.phone_number}</td>
                                    <td style={styles.td}>{owner.email}</td>
                                    <td style={styles.td}>
                                        <button
                                            onClick={() => {
                                                setEditingOwner(owner);
                                                setShowModal(true);
                                            }}
                                            style={{ ...styles.actionButton, ...styles.editButton }}
                                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(owner)}
                                            style={{ ...styles.actionButton, ...styles.deleteButton }}
                                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Owner Create/Edit Modal */}
            <OwnerModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditingOwner(null);
                }}
                owner={editingOwner}
                onSave={handleSave}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                owner={deleteConfirm}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default Owners;
